import { useState, useEffect, useRef, useContext } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { serverTimestamp, updateDoc, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase.config';
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import LoadingContext from '../contexts/loading/LoadingContext'
import { Helmet } from 'react-helmet'
import { Card, FormHelperText } from '@chakra-ui/react';

import Spinner from '../components/feedback/Spinner'
import FormRadio from '../components/form/FormRadio'
import FormSelect from '../components/form/FormSelect'
import FormNumber from '../components/form/FormNumber';
import FormCheckbox from '../components/form/FormCheckbox';

import {
  Button,
  FormControl,
  FormLabel,
  Switch,
  Input,
  NumberInput,
  Textarea,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Heading,
  Radio,
  RadioGroup,
  useRadioGroup,
  Stack,
  HStack,
  useToast,
  Box,
  Text,
  Icon,
  SimpleGrid,
  Divider,
  Alert,
  AlertIcon,
  Badge,
  Select,
  Tooltip
} from '@chakra-ui/react'

import { carsData } from '../data/carsData';
import { cities } from '../data/citiesData';
import { colorsData } from '../data/colorsData'
import { FaCar, FaClock, FaMoneyBillWave, FaRoute, FaUsers, FaCalendarAlt, FaChair, FaLuggageCart, FaMapMarkerAlt, FaInfoCircle, FaExchangeAlt } from 'react-icons/fa'

// Pakistan major cities for ride sharing
const pakistanCities = [
  "Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad",
  "Multan", "Peshawar", "Quetta", "Gujranwala", "Sialkot",
  "Hyderabad", "Abbottabad", "Bahawalpur", "Sargodha", "Sukkur", "Swat", "Gilgit", "Skardu", "Muzaffarabad", "Mardan", "Taxila", "Dir", "Chitral", "Nowshehra", "Swabi", "Malakand"
];

const EditListing = () => {
  const [formData, setFormData] = useState({
    type: 'sale',
    manufacturer: '',
    modelName: '',
    offer: false,
    transmission: 'Automatic',
    modelYear: '2022',
    color: '',
    location: '',
    city: '',
    mileage: '',
    price: '',
    discountedPrice: '',
    notes: '',
    images: {},
    phoneNumber: '',
    options: {
      leatherSeats: false,
      airBags: false,
      bluetooth: false,
      fogLights: false,
      ac: true,
      usbPort: false
    },
    userRef: '',
    // Sale specific - Installments (no interest rate)
    installmentsAvailable: false,
    downPayment: '',
    monthlyInstallment: '',
    installmentPeriod: '',
    // Sale specific - Exchange
    exchangeAvailable: false,
    exchangeManufacturer: '',
    exchangeModel: '',
    exchangeYear: '',
    exchangeCondition: '',
    exchangeMileage: '',
    // Ride specific
    fromCity: '',
    toCity: '',
    availableSeats: 4,
    departureDate: '',
    departureTime: '',
    rideType: 'one-way',
    luggageSpace: 'Medium',
    smoking: false,
    pets: false,
    femaleOnly: false,
    returnDate: '',
    returnTime: '',
    pickupLocation: '', // Specific pickup location for ride sharing
  })
  const { buttonLoading, spinnerLoading, dispatch } = useContext(LoadingContext)
  const [listing, setListing] = useState(false)
  const [existingImages, setExistingImages] = useState([])

  const options = ['Manual', 'Automatic']
  const { getRootProps, getRadioProps} = useRadioGroup({
    name: 'framework',
    defaultValue: 'Automatic'
  })
  const group = getRootProps()
  
  const toast = useToast()
  const isMounted = useRef(true)
  const auth = getAuth()
  const navigate = useNavigate()
  const params = useParams()
  
  const formatPrice = (val) => `₨ ` + new Intl.NumberFormat('en-PK').format(val || 0)
  const parsePrice = (val) => parseInt(String(val).replace(/[^0-9]/g, '')) || 0
  
  useEffect(() => {
    if (isMounted) {
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    userRef: user.uid
                }))
                dispatch({ type: 'STOP_SPINNER_LOADING' })
            } else {
                navigate('/sign-in')
            }
        })
    }
    return () => {
        isMounted.current = false
    }
  }, [isMounted, auth, navigate, dispatch])
  

  useEffect(() => {
    const fetchUserListing = async () => {
        const docRef = doc(db, 'cars', params.listingId)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
            const listingData = docSnap.data()
            setListing(listingData)
            setExistingImages(listingData.carImages || [])
            setFormData({...listingData})
        } else {
            navigate('/')
            toast({
                title: 'Error',
                description: "The listing does not exist",
                position: 'top',
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
        }
    }
    fetchUserListing()
  }, [params.listingId, navigate, toast])

  const changeHandler = (e) => {
      if (!e.target.files) {
        setFormData(prev => ({
            ...prev,
            [e.target.id]: e.target.value
        }))
      } else if (e.target.files) {
          setFormData(prev => ({
              ...prev,
              images: e.target.files
          }))
      }
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    dispatch({ type: 'START_LOADING' })

    try {
      let formDataCopy = {...formData}
      
      // Handle image upload if images changed
      if(formData.images && Object.keys(formData.images).length > 0 && formData.images !== listing?.images) {
        const storeImage = async (img) => {
          return new Promise((resolve, reject) => {
              const storage = getStorage()
              const fileName = `${auth.currentUser.uid}-${img.name}-${uuidv4()}`
              const storageRef = ref(storage, 'images/' + fileName)
              const uploadTask = uploadBytesResumable(storageRef, img)

              uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                console.log('Upload is ' + progress + '% done')
              }, 
              (error) => {
                reject(error)
              }, 
              () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                  resolve(downloadURL)
                })
              })
          })
        }

        const newImages = await Promise.all(
            [...formData.images].map(img => storeImage(img))
        )
        
        formDataCopy.carImages = [...existingImages, ...newImages]
      } else {
        formDataCopy.carImages = existingImages
      }

      delete formDataCopy.images

      const docRef = doc(db, 'cars', params.listingId)
      await updateDoc(docRef, formDataCopy)

      dispatch({ type: 'STOP_LOADING' })
      toast({
        title: 'Success!',
        description: "Your listing has been updated successfully",
        position: 'top',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate(`/category/${formDataCopy.type}/${docRef.id}`)

    } catch (error) {
      console.log(error.code)
      dispatch({ type: 'STOP_LOADING' })
      toast({
        title: 'Error',
        description: "Something went wrong!",
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const isRideCategory = formData.type === 'ride'
  const isRentCategory = formData.type === 'rent'
  const isSaleCategory = formData.type === 'sale'

  if (spinnerLoading) {
      return <Spinner />
  }

  return (
    <>
      <Helmet>
        <title>Edit Listing | NCPWheels</title>
        <meta name="description" content="Edit your vehicle listing on NCPWheels" />
      </Helmet>
      
      <form onSubmit={submitHandler}>
        <Box maxW="container.lg" mx="auto" p={4} py={8}>
          <Heading mb={6} display="flex" alignItems="center" color="blue.700">
            <Icon as={isRideCategory ? FaRoute : isRentCategory ? FaClock : FaCar} 
                  mr={3} 
                  color="blue.500" 
                  boxSize={8} />
            Edit {isRideCategory ? 'Ride Sharing' : isRentCategory ? 'Rental' : 'Sale'} Listing
          </Heading>

          <Alert status="info" mb={6} borderRadius="lg" bg="blue.50">
            <AlertIcon color="blue.500" />
            Update your listing details below. All fields marked with * are required.
          </Alert>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormSelect
              name='Manufacturer'
              id='manufacturer'
              value={formData.manufacturer}
              handler={changeHandler}
              selectData={carsData}
              dataKey='manufacturer'
              isRequired={!isRideCategory}
            />

            {!isRideCategory && (
              <FormControl isRequired>
                <FormLabel>Model Name</FormLabel>
                <Input
                  variant='flushed'
                  id='modelName'
                  type='text'
                  value={formData.modelName}
                  onChange={changeHandler}
                  placeholder='ex. Civic 2022'
                  focusBorderColor="blue.500"
                />
              </FormControl>
            )}

            {isRideCategory && (
              <>
                <FormControl isRequired>
                  <FormLabel display="flex" alignItems="center" gap={2}>
                    <Icon as={FaMapMarkerAlt} color="blue.500" />
                    From City (Pickup)
                    <Tooltip label="City where the ride will start">
                      <Icon as={FaInfoCircle} color="gray.500" boxSize={3} />
                    </Tooltip>
                  </FormLabel>
                  <Select
                    value={formData.fromCity}
                    onChange={(e) => setFormData(prev => ({ ...prev, fromCity: e.target.value }))}
                    focusBorderColor="blue.500"
                    placeholder="Select departure city"
                  >
                    {pakistanCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel display="flex" alignItems="center" gap={2}>
                    <Icon as={FaRoute} color="blue.500" />
                    To City (Destination)
                    <Tooltip label="Destination city for the ride">
                      <Icon as={FaInfoCircle} color="gray.500" boxSize={3} />
                    </Tooltip>
                  </FormLabel>
                  <Select
                    value={formData.toCity}
                    onChange={(e) => setFormData(prev => ({ ...prev, toCity: e.target.value }))}
                    focusBorderColor="blue.500"
                    placeholder="Select destination city"
                  >
                    {pakistanCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <FormSelect
              name='Color'
              id='color'
              value={formData.color}
              handler={changeHandler}
              selectData={colorsData}
              dataKey='color'
            />  

            {!isRideCategory && (
              <FormNumber
                labelText='Model Year'
                inputValue={formData.modelYear}
                max='2024'
                min='1920'
                onChange={(valueString) => setFormData(prev => ({
                  ...prev,
                  modelYear: valueString
                }))} 
              />
            )}
            
            {!isRideCategory && (
              <FormNumber
                labelText='Mileage (km)'
                inputValue={formData.mileage}
                min='0'
                onChange={(valueString) => setFormData(prev => ({
                  ...prev,
                  mileage: valueString
                }))} 
              />
            )}

            {!isRideCategory && (
              <FormControl>
                <FormLabel>Transmission</FormLabel>
                <HStack onChange={(value) => setFormData(prev => ({
                  ...prev,
                  transmission: value.target.defaultValue
                }))} 
                {...group}>
                {options.map((value) => {
                  const radio = getRadioProps({ value })
                  return (
                    <FormRadio key={value} {...radio}>
                      {value}
                    </FormRadio>
                  )
                })}
                </HStack>
              </FormControl>
            )}
          </SimpleGrid>

          <Divider my={6} />

          {/* Listing Type Selection */}
          <FormControl mb={6}>
            <FormLabel fontWeight="semibold">Listing Type</FormLabel>
            <RadioGroup 
              onChange={(value) => setFormData(prev => ({
                ...prev,
                type: value
              }))} 
              value={formData.type}
            >
              <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                <Radio colorScheme='blue' value='sale'>For Sale</Radio>
                <Radio colorScheme='blue' value='rent'>For Rent</Radio>
                <Radio colorScheme='blue' value='ride'>Ride Sharing</Radio>
              </Stack>
            </RadioGroup> 
          </FormControl>

          {/* Price Section */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormNumber
              labelText={isRideCategory ? 'Price Per Seat (PKR)' : isRentCategory ? 'Price Per Day (PKR)' : 'Price (PKR)'}
              inputValue={formData.price}
              min='0'
              onChange={(value) => setFormData(prev => ({
                ...prev,
                price: parsePrice(value)
              }))}
            />

            {isRideCategory && (
              <FormNumber
                labelText='Available Seats'
                inputValue={formData.availableSeats}
                min='1'
                max='7'
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  availableSeats: parseInt(value)
                }))}
              />
            )}
          </SimpleGrid>

          {/* Sale Specific Fields */}
          {isSaleCategory && (
            <>
              <FormControl mb={4} display='flex' alignItems='center'>
                <FormLabel htmlFor='offer' mb='0'>
                  Offer Discount
                </FormLabel>
                <Switch 
                  colorScheme='blue' 
                  id='offer' 
                  size='lg' 
                  isChecked={formData.offer} 
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    offer: !prev.offer
                  }))}        
                />
              </FormControl>
              
              {formData.offer && (
                <FormControl mb={4}>
                  <FormLabel htmlFor='discountedPrice'>Discounted Price (PKR)</FormLabel>
                  <NumberInput
                    variant='flushed'
                    min={0}
                    max={formData.price} 
                    onChange={(value) => setFormData(prev => ({
                      ...prev,
                      discountedPrice: parsePrice(value)
                    }))} 
                    value={formData.discountedPrice}
                  >
                    <NumberInputField id='discountedPrice' focusBorderColor="blue.500" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}

              {/* Installment Options - No interest rate */}
              <FormControl mb={4} display='flex' alignItems='center'>
                <FormLabel htmlFor='installments' mb='0'>
                  Installments Available
                </FormLabel>
                <Switch 
                  colorScheme='green' 
                  id='installments' 
                  size='lg' 
                  isChecked={formData.installmentsAvailable} 
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    installmentsAvailable: !prev.installmentsAvailable
                  }))}        
                />
              </FormControl>

              {formData.installmentsAvailable && (
                <Card bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200" mb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormNumber
                      labelText='Down Payment (PKR)'
                      inputValue={formData.downPayment}
                      min='0'
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        downPayment: parsePrice(value)
                      }))}
                    />
                    <FormNumber
                      labelText='Monthly Installment (PKR)'
                      inputValue={formData.monthlyInstallment}
                      min='0'
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        monthlyInstallment: parsePrice(value)
                      }))}
                    />
                    <FormNumber
                      labelText='Installment Period (months)'
                      inputValue={formData.installmentPeriod}
                      min='3'
                      max='84'
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        installmentPeriod: parseInt(value)
                      }))}
                    />
                  </SimpleGrid>
                </Card>
              )}

              {/* Exchange Options - Full exchange functionality */}
              <FormControl mb={4} display='flex' alignItems='center'>
                <FormLabel htmlFor='exchange' mb='0'>
                  Accept Exchange
                </FormLabel>
                <Switch 
                  colorScheme='purple' 
                  id='exchange' 
                  size='lg' 
                  isChecked={formData.exchangeAvailable} 
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    exchangeAvailable: !prev.exchangeAvailable
                  }))}        
                />
              </FormControl>

              {formData.exchangeAvailable && (
                <Card bg="purple.50" p={4} borderRadius="lg" border="1px solid" borderColor="purple.200" mb={4}>
                  <Text fontWeight="semibold" mb={3} display="flex" alignItems="center" gap={2}>
                    <Icon as={FaExchangeAlt} color="purple.600" />
                    What car are you looking for in exchange?
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormSelect
                      name='Preferred Manufacturer'
                      id='exchangeManufacturer'
                      value={formData.exchangeManufacturer}
                      handler={changeHandler}
                      selectData={carsData}
                      dataKey='manufacturer'
                      placeholder="Select manufacturer"
                      isRequired
                    />
                    <FormControl>
                      <FormLabel>Preferred Model</FormLabel>
                      <Input
                        variant='flushed'
                        id='exchangeModel'
                        value={formData.exchangeModel}
                        onChange={changeHandler}
                        placeholder="e.g., Civic, Corolla (Optional)"
                        focusBorderColor="purple.500"
                      />
                    </FormControl>
                    <FormNumber
                      labelText='Preferred Year'
                      inputValue={formData.exchangeYear}
                      min='1920'
                      max='2024'
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        exchangeYear: value
                      }))}
                    />
                    <FormNumber
                      labelText='Max Mileage (km)'
                      inputValue={formData.exchangeMileage}
                      min='0'
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        exchangeMileage: value
                      }))}
                    />
                    <FormSelect
                      name='Minimum Condition'
                      id='exchangeCondition'
                      value={formData.exchangeCondition}
                      handler={changeHandler}
                      selectData={[
                        { condition: 'excellent' },
                        { condition: 'good' },
                        { condition: 'fair' }
                      ]}
                      dataKey='condition'
                      placeholder="Select condition"
                    />
                  </SimpleGrid>
                  <Alert status="info" mt={4} size="sm" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">Let potential buyers know what car you're looking for in exchange</Text>
                  </Alert>
                </Card>
              )}
            </>
          )}

          {/* Ride Sharing Specific Fields */}
          {isRideCategory && (
            <>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired>
                  <FormLabel>Ride Type</FormLabel>
                  <RadioGroup
                    value={formData.rideType}
                    onChange={(value) => setFormData(prev => ({ ...prev, rideType: value }))}
                  >
                    <Stack direction="row" spacing={4}>
                      <Radio colorScheme='blue' value="one-way">One Way</Radio>
                      <Radio colorScheme='blue' value="round-trip">Round Trip</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Departure Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.departureDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, departureDate: e.target.value }))}
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Departure Time</FormLabel>
                  <Input
                    type="time"
                    value={formData.departureTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, departureTime: e.target.value }))}
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Luggage Space</FormLabel>
                  <Select
                    value={formData.luggageSpace}
                    onChange={(e) => setFormData(prev => ({ ...prev, luggageSpace: e.target.value }))}
                    focusBorderColor="blue.500"
                  >
                    <option value="Small">Small (Carry-on only)</option>
                    <option value="Medium">Medium (1-2 bags)</option>
                    <option value="Large">Large (3+ bags)</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              {formData.rideType === "round-trip" && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                  <FormControl>
                    <FormLabel>Return Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.returnDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, returnDate: e.target.value }))}
                      focusBorderColor="blue.500"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Return Time</FormLabel>
                    <Input
                      type="time"
                      value={formData.returnTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, returnTime: e.target.value }))}
                      focusBorderColor="blue.500"
                    />
                  </FormControl>
                </SimpleGrid>
              )}

              {/* Rider Preferences */}
              <Box mt={4} p={4} bg="gray.50" borderRadius="lg">
                <FormLabel fontWeight="semibold">Rider Preferences</FormLabel>
                <Stack direction="row" spacing={6} flexWrap="wrap">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" mr={3}>No Smoking</FormLabel>
                    <Switch 
                      colorScheme='blue' 
                      isChecked={formData.smoking} 
                      onChange={() => setFormData(prev => ({ ...prev, smoking: !prev.smoking }))}
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" mr={3}>Pets Allowed</FormLabel>
                    <Switch 
                      colorScheme='blue' 
                      isChecked={formData.pets} 
                      onChange={() => setFormData(prev => ({ ...prev, pets: !prev.pets }))}
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" mr={3}>Female Only</FormLabel>
                    <Switch 
                      colorScheme='pink' 
                      isChecked={formData.femaleOnly} 
                      onChange={() => setFormData(prev => ({ ...prev, femaleOnly: !prev.femaleOnly }))}
                    />
                  </FormControl>
                </Stack>
              </Box>
            </>
          )}

          <Divider my={6} />

          {/* Location Fields */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {isRideCategory ? (
              <>
                <FormControl>
                  <FormLabel display="flex" alignItems="center" gap={2}>
                    <Icon as={FaMapMarkerAlt} color="blue.500" />
                    Pickup City (Auto-set)
                  </FormLabel>
                  <Input
                    value={formData.fromCity || "Not selected"}
                    isReadOnly
                    variant='flushed'
                    bg="gray.50"
                  />
                  <FormHelperText>From your selected route</FormHelperText>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel display="flex" alignItems="center" gap={2}>
                    <Icon as={FaMapMarkerAlt} color="blue.500" />
                    Exact Pickup Location
                  </FormLabel>
                  <Input
                    variant='flushed'
                    id='pickupLocation'
                    value={formData.pickupLocation}
                    onChange={changeHandler}
                    placeholder='e.g., Main Bus Stand, City Mall Entrance, Shell Petrol Pump'
                    focusBorderColor="blue.500"
                  />
                  <FormHelperText>Be specific - helps passengers find you easily</FormHelperText>
                </FormControl>
              </>
            ) : (
              <>
                <FormSelect
                  name='City'
                  id='city'
                  value={formData.city}
                  handler={changeHandler}
                  selectData={cities}
                  dataKey='city'
                  isRequired
                />
                
                <FormControl isRequired>
                  <FormLabel htmlFor='location'>Location Details</FormLabel>
                  <Input
                    variant='flushed' 
                    value={formData.location} 
                    onChange={changeHandler} 
                    id='location' 
                    type='text' 
                    placeholder='Neighborhood or exact location'
                    focusBorderColor="blue.500"
                  />
                </FormControl>
              </>
            )}
          </SimpleGrid>
          
          <FormControl mb={6} mt={4}>
            <FormLabel htmlFor='notes'>Additional Notes</FormLabel>
            <Textarea
              variant='flushed'
              id='notes'
              value={formData.notes}
              onChange={changeHandler}
              placeholder={isRideCategory 
                ? 'Tell passengers about meeting arrangements, special requests...' 
                : isSaleCategory && formData.exchangeAvailable
                ? 'Tell potential buyers about your exchange preferences, what you\'re looking for, etc...'
                : 'Tell potential buyers/renters about your vehicle...'}
              size='sm'
              rows={4}
              focusBorderColor="blue.500"
            />
          </FormControl>

          <FormControl mb={6}>
            <FormLabel>Phone Number</FormLabel>
            <Input
              variant='flushed'
              id='phoneNumber'
              type='tel'
              value={formData.phoneNumber}
              onChange={changeHandler}
              placeholder='+92 XXX XXXXXXX'
              focusBorderColor="blue.500"
              isRequired
            />
          </FormControl>

          {/* Features - For Sale/Rent only */}
          {!isRideCategory && (
            <FormControl mb={6}>
              <FormLabel>Features & Options</FormLabel>
              <Stack direction={['column', 'row']} spacing='15px' wrap="wrap">
                <FormCheckbox
                  checkboxValue='leatherSeats'
                  checkStatus={formData.options?.leatherSeats}
                  text='Leather Seats'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      leatherSeats: !prev.options?.leatherSeats
                    }
                  }))}
                />
                <FormCheckbox
                  checkboxValue='airBags'
                  checkStatus={formData.options?.airBags}
                  text='Air Bags'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      airBags: !prev.options?.airBags
                    }
                  }))}
                />
                <FormCheckbox
                  checkboxValue='bluetooth'
                  checkStatus={formData.options?.bluetooth}
                  text='Bluetooth'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      bluetooth: !prev.options?.bluetooth
                    }
                  }))}
                />
                <FormCheckbox
                  checkboxValue='fogLights'
                  checkStatus={formData.options?.fogLights}
                  text='Fog Lights'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      fogLights: !prev.options?.fogLights
                    }
                  }))}
                />
                <FormCheckbox
                  checkboxValue='ac'
                  checkStatus={formData.options?.ac}
                  text='Air Conditioning'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      ac: !prev.options?.ac
                    }
                  }))}
                />
                <FormCheckbox
                  checkboxValue='usbPort'
                  checkStatus={formData.options?.usbPort}
                  text='USB Port'
                  change={() => setFormData(prev => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      usbPort: !prev.options?.usbPort
                    }
                  }))}
                />
              </Stack>
            </FormControl>
          )}

          <FormControl mb={6}>
            <FormLabel>Vehicle Images</FormLabel>
            <Text fontSize="sm" color="gray.500" mb={2}>
              First image will be used as cover (max 6 images). Leave empty to keep existing images.
            </Text>
            {existingImages.length > 0 && (
              <Box mb={3}>
                <Badge colorScheme="blue" mb={2}>Existing Images: {existingImages.length}</Badge>
                <Text fontSize="xs" color="gray.500">Adding new images will append to existing ones.</Text>
              </Box>
            )}
            <Input
              type='file'
              id='images'
              accept='.jpg,.png,.jpeg'
              multiple
              onChange={changeHandler}
              border="none"
              p={1}
              pt={2}
            />
          </FormControl>
          
          <Button 
            isLoading={buttonLoading}
            loadingText='Updating...'
            type='submit' 
            colorScheme='blue' 
            size='lg'
            width="full"
            mb={4}
            mt={2}
          >
            Update Listing
          </Button>
        </Box>
      </form>
    </>
  )
}

export default EditListing